import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { categoriesService } from '../services/categories';
import { productsService } from '../services/products';
import { frenchFilterConfig, isProductFrench, isCategoryFrench } from '../config/french-filter';
import { 
  AdjustmentsHorizontalIcon, 
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

export const FrenchFilterConfigPage: React.FC = () => {
  const [testMode, setTestMode] = useState(false);
  
  // Récupérer quelques produits pour tester
  const { data: productsData } = useQuery({
    queryKey: ['products', 'test'],
    queryFn: () => productsService.getProducts({ per_page: 10 }),
    enabled: testMode,
  });
  
  // Récupérer quelques catégories pour tester
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', 'test'],
    queryFn: () => categoriesService.getCategories({ per_page: 10 }),
    enabled: testMode,
  });

  const currentConfig = {
    enabled: frenchFilterConfig.enabled,
    filterType: frenchFilterConfig.filterType,
    language: frenchFilterConfig.language,
    categorySlug: frenchFilterConfig.categorySlug,
    categoryId: frenchFilterConfig.categoryId,
    tagSlug: frenchFilterConfig.tagSlug,
    tagId: frenchFilterConfig.tagId,
    attributeName: frenchFilterConfig.attributeName,
    attributeValue: frenchFilterConfig.attributeValue,
    metaKey: frenchFilterConfig.metaKey,
    metaValue: frenchFilterConfig.metaValue,
  };

  return (
    <div className="container-custom py-12">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-8 flex items-center gap-3">
          <AdjustmentsHorizontalIcon className="h-8 w-8 text-primary-600" />
          Configuration du filtre français
        </h1>

        {/* État actuel */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold mb-4">Configuration actuelle</h2>
          
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-medium">Filtre activé :</span>
              {currentConfig.enabled ? (
                <span className="flex items-center gap-1 text-green-600">
                  <CheckCircleIcon className="h-5 w-5" />
                  Oui
                </span>
              ) : (
                <span className="flex items-center gap-1 text-red-600">
                  <XCircleIcon className="h-5 w-5" />
                  Non
                </span>
              )}
            </div>
            
            <div>
              <span className="font-medium">Type de filtre :</span>
              <span className="ml-2 px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-sm">
                {currentConfig.filterType}
              </span>
            </div>

            {currentConfig.filterType === 'lang' && (
              <div>
                <span className="font-medium">Code de langue :</span>
                <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                  {currentConfig.language || 'Non défini'}
                </span>
              </div>
            )}

            {currentConfig.filterType === 'category' && (
              <>
                <div>
                  <span className="font-medium">Slug de la catégorie :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.categorySlug || 'Non défini'}
                  </span>
                </div>
                <div>
                  <span className="font-medium">ID de la catégorie :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.categoryId || 'Non défini'}
                  </span>
                </div>
              </>
            )}

            {currentConfig.filterType === 'tag' && (
              <>
                <div>
                  <span className="font-medium">Slug du tag :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.tagSlug || 'Non défini'}
                  </span>
                </div>
                <div>
                  <span className="font-medium">ID du tag :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.tagId || 'Non défini'}
                  </span>
                </div>
              </>
            )}

            {currentConfig.filterType === 'attribute' && (
              <>
                <div>
                  <span className="font-medium">Nom de l'attribut :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.attributeName || 'Non défini'}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Valeur de l'attribut :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.attributeValue || 'Non défini'}
                  </span>
                </div>
              </>
            )}

            {currentConfig.filterType === 'meta' && (
              <>
                <div>
                  <span className="font-medium">Clé meta :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.metaKey || 'Non défini'}
                  </span>
                </div>
                <div>
                  <span className="font-medium">Valeur meta :</span>
                  <span className="ml-2 font-mono bg-gray-100 px-2 py-1 rounded">
                    {currentConfig.metaValue || 'Non défini'}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Instructions de configuration */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
          <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
            <InformationCircleIcon className="h-5 w-5" />
            Comment configurer le filtre
          </h3>
          
          <div className="space-y-3 text-sm text-blue-800">
            <p>
              Pour configurer le filtre français, modifiez votre fichier <code className="bg-blue-100 px-2 py-1 rounded">.env</code> :
            </p>
            
            <div className="bg-white rounded p-4 font-mono text-xs">
              <p># Type de filtre : lang, category, tag, attribute, meta, custom</p>
              <p>REACT_APP_FRENCH_FILTER_TYPE={currentConfig.filterType}</p>
              
              {currentConfig.filterType === 'lang' && (
                <>
                  <p className="mt-2"># Pour filtrer par langue (WPML/Polylang)</p>
                  <p>REACT_APP_FRENCH_LANGUAGE=fr</p>
                </>
              )}
              
              {currentConfig.filterType === 'category' && (
                <>
                  <p className="mt-2"># Pour filtrer par catégorie</p>
                  <p>REACT_APP_FRENCH_CATEGORY_SLUG=francais</p>
                  <p># ou</p>
                  <p>REACT_APP_FRENCH_CATEGORY_ID=123</p>
                </>
              )}
              
              {currentConfig.filterType === 'tag' && (
                <>
                  <p className="mt-2"># Pour filtrer par tag</p>
                  <p>REACT_APP_FRENCH_TAG_SLUG=francais</p>
                  <p># ou</p>
                  <p>REACT_APP_FRENCH_TAG_ID=456</p>
                </>
              )}
              
              {currentConfig.filterType === 'attribute' && (
                <>
                  <p className="mt-2"># Pour filtrer par attribut</p>
                  <p>REACT_APP_FRENCH_ATTRIBUTE_NAME=langue</p>
                  <p>REACT_APP_FRENCH_ATTRIBUTE_VALUE=francais</p>
                </>
              )}
              
              {currentConfig.filterType === 'meta' && (
                <>
                  <p className="mt-2"># Pour filtrer par métadonnées</p>
                  <p>REACT_APP_FRENCH_META_KEY=language</p>
                  <p>REACT_APP_FRENCH_META_VALUE=fr</p>
                </>
              )}
            </div>
            
            <p className="mt-3">
              Après avoir modifié le fichier <code className="bg-blue-100 px-2 py-1 rounded">.env</code>, 
              redémarrez l'application avec <code className="bg-blue-100 px-2 py-1 rounded">npm start</code>.
            </p>
          </div>
        </div>

        {/* Test du filtre */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Tester le filtre</h2>
          
          <button
            onClick={() => setTestMode(!testMode)}
            className={`mb-4 ${testMode ? 'btn-secondary' : 'btn-primary'}`}
          >
            {testMode ? 'Arrêter le test' : 'Démarrer le test'}
          </button>

          {testMode && (
            <div className="space-y-6">
              {/* Test des produits */}
              <div>
                <h3 className="font-semibold mb-3">Produits filtrés</h3>
                {productsData ? (
                  <div className="space-y-2">
                    {productsData.products.map((product) => {
                      const isFrench = isProductFrench(product);
                      return (
                        <div
                          key={product.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isFrench ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div>
                            <p className="font-medium">{product.name}</p>
                            {product.lang && (
                              <p className="text-sm text-gray-600">
                                Langue : <span className="font-mono bg-gray-100 px-1 rounded">{product.lang}</span>
                              </p>
                            )}
                            <p className="text-sm text-gray-600">
                              Catégories : {product.categories.map(c => c.name).join(', ')}
                            </p>
                            {product.tags.length > 0 && (
                              <p className="text-sm text-gray-600">
                                Tags : {product.tags.map(t => t.name).join(', ')}
                              </p>
                            )}
                          </div>
                          <div>
                            {isFrench ? (
                              <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-6 w-6 text-red-600" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500">Chargement des produits...</p>
                )}
              </div>

              {/* Test des catégories */}
              <div>
                <h3 className="font-semibold mb-3">Catégories filtrées</h3>
                {categoriesData ? (
                  <div className="space-y-2">
                    {categoriesData.categories.map((category) => {
                      const isFrench = isCategoryFrench(category);
                      return (
                        <div
                          key={category.id}
                          className={`flex items-center justify-between p-3 rounded-lg border ${
                            isFrench ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div>
                            <p className="font-medium">{category.name}</p>
                            <p className="text-sm text-gray-600">
                              Slug : {category.slug} | ID : {category.id}
                              {category.lang && <> | Langue : <span className="font-mono bg-gray-100 px-1 rounded">{category.lang}</span></>}
                            </p>
                          </div>
                          <div>
                            {isFrench ? (
                              <CheckCircleIcon className="h-6 w-6 text-green-600" />
                            ) : (
                              <XCircleIcon className="h-6 w-6 text-red-600" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500">Chargement des catégories...</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
